/**
 * useBadgeSignals — T12.5.1 (C/E). The household's preference signal sets,
 * partitioned by axis × system × value, that drive the stacked badge set on BOTH
 * the Deals surface and the Planning list. One hook so the two stay in lockstep.
 *
 * Reads three queries:
 *   - household-wide brand like/avoid  → ★ Liked / ⊘ Avoided brand
 *   - household-wide product like/avoid→ ★ Liked / ⊘ Avoided product
 *   - per-product (catalog) preferences→ ★ Preferred / ◇ Substitute / ⊘ Excluded
 * plus the brand catalog to resolve brand_id → name (brand rows carry ids, the
 * deal/offer carries a name string).
 *
 * Returns `Omit<BadgeInputs, 'onList'>` — the caller adds the per-card on-list
 * flag (product-level for Deals; always true for a Planning offer row).
 */

import { useMemo } from 'react';
import {
  useBrands,
  useHouseholdBrandPreferences,
  useHouseholdSystemProductPreferences,
  useHouseholdCatalogPreferenceSignals,
} from '@shopista/shared/api/hooks';
import { norm, type BadgeInputs } from './badges';

export type BadgeSignals = Omit<BadgeInputs, 'onList'>;

export function useBadgeSignals(): BadgeSignals {
  const brandPrefsQuery = useHouseholdBrandPreferences();
  const brandsQuery = useBrands();
  const systemProductPrefsQuery = useHouseholdSystemProductPreferences();
  const catalogSignalsQuery = useHouseholdCatalogPreferenceSignals();

  const brandIdToName = useMemo(
    () => new Map((brandsQuery.data?.brands ?? []).map((b) => [b.id, norm(b.name)])),
    [brandsQuery.data],
  );

  return useMemo<BadgeSignals>(() => {
    const likedBrandNames = new Set<string>();
    const avoidedBrandNames = new Set<string>();
    const likedProductIds = new Set<string>();
    const likedProductNames = new Set<string>();
    const avoidedProductIds = new Set<string>();
    const avoidedProductNames = new Set<string>();
    const preferredBrandNames = new Set<string>();
    const substituteBrandNames = new Set<string>();
    const excludedBrandNames = new Set<string>();
    const preferredProductIds = new Set<string>();
    const preferredProductNames = new Set<string>();
    const substituteProductIds = new Set<string>();
    const substituteProductNames = new Set<string>();
    const excludedProductIds = new Set<string>();
    const excludedProductNames = new Set<string>();

    for (const pref of brandPrefsQuery.data?.preferences ?? []) {
      const name = brandIdToName.get(pref.brand_id);
      if (!name) continue;
      if (pref.preference === 'like') likedBrandNames.add(name);
      else if (pref.preference === 'avoid') avoidedBrandNames.add(name);
    }
    for (const pref of systemProductPrefsQuery.data?.preferences ?? []) {
      const n = norm(pref.name);
      if (pref.preference === 'like') {
        likedProductIds.add(pref.systemproduct_id);
        if (n) likedProductNames.add(n);
      } else if (pref.preference === 'avoid') {
        avoidedProductIds.add(pref.systemproduct_id);
        if (n) avoidedProductNames.add(n);
      }
    }
    for (const b of catalogSignalsQuery.data?.brands ?? []) {
      const name = brandIdToName.get(b.brand_id);
      if (!name) continue;
      if (b.preference === 'preferred') preferredBrandNames.add(name);
      else if (b.preference === 'substitute') substituteBrandNames.add(name);
      else if (b.preference === 'exclude') excludedBrandNames.add(name);
    }
    for (const p of catalogSignalsQuery.data?.products ?? []) {
      const n = norm(p.name);
      if (p.preference === 'preferred') {
        preferredProductIds.add(p.systemproduct_id);
        if (n) preferredProductNames.add(n);
      } else if (p.preference === 'substitute') {
        substituteProductIds.add(p.systemproduct_id);
        if (n) substituteProductNames.add(n);
      } else if (p.preference === 'exclude') {
        excludedProductIds.add(p.systemproduct_id);
        if (n) excludedProductNames.add(n);
      }
    }

    return {
      likedBrandNames,
      avoidedBrandNames,
      likedProductIds,
      likedProductNames,
      avoidedProductIds,
      avoidedProductNames,
      preferredBrandNames,
      substituteBrandNames,
      excludedBrandNames,
      preferredProductIds,
      preferredProductNames,
      substituteProductIds,
      substituteProductNames,
      excludedProductIds,
      excludedProductNames,
    };
  }, [
    brandPrefsQuery.data,
    systemProductPrefsQuery.data,
    catalogSignalsQuery.data,
    brandIdToName,
  ]);
}
